"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";

export const LANGUAGE_LEVEL_OPTIONS = [
  {
    value: "absolute_beginner",
    label: "New to the language",
    compactLabel: "First steps",
    description: "First words, simple replies, and plenty of support.",
    startLabel: "Start speaking from the first steps",
  },
  {
    value: "beginner",
    label: "Beginner",
    compactLabel: "Beginner",
    description: "Short answers and the sentence patterns you need most.",
    startLabel: "Start speaking at Beginner level",
  },
  {
    value: "intermediate",
    label: "Intermediate",
    compactLabel: "Intermediate",
    description: "Longer conversations with useful, focused corrections.",
    startLabel: "Start speaking at Intermediate level",
  },
  {
    value: "advanced",
    label: "Advanced",
    compactLabel: "Advanced",
    description: "Build fluency, precision, and confidence in nuanced topics.",
    startLabel: "Start speaking at Advanced level",
  },
] as const;

export const PRACTICE_START_LABEL = "Start speaking";

export type LanguageLevel = (typeof LANGUAGE_LEVEL_OPTIONS)[number]["value"];

export function getLanguageLevelOption(value: LanguageLevel) {
  return LANGUAGE_LEVEL_OPTIONS.find((level) => level.value === value) || LANGUAGE_LEVEL_OPTIONS[2];
}

export function getPracticeLevelStartLabel(value: LanguageLevel) {
  return getLanguageLevelOption(value).startLabel;
}

type PracticeLevelSelectorProps = {
  value: LanguageLevel;
  onValueChange: (value: LanguageLevel) => void;
  variant?: "default" | "ecole" | "liceo";
  /** "inline": the label and a pill trigger on one line, for the first-run intro. */
  appearance?: "panel" | "inline";
};

export function PracticeLevelSelector({
  value,
  onValueChange,
  variant = "default",
  appearance = "panel",
}: PracticeLevelSelectorProps) {
  const selectedLevel = getLanguageLevelOption(value);
  const isEcole = variant !== "default";
  const inline = appearance === "inline";

  return (
    <div
      className={
        inline
          ? "flex items-center justify-center gap-3"
          : `rounded-lg border bg-black/25 p-3 text-left transition-colors sm:p-3.5 ${
              isEcole ? "border-school-blue/35" : "border-white/10"
            }`
      }
      data-practice-level-selector
    >
      {inline ? (
        <span className="text-[13px] font-medium text-white/50">Your level</span>
      ) : (
        <div className="mb-2 flex items-center">
          <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/45">
            Your level
          </span>
        </div>
      )}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={
              inline
                ? "inline-flex min-h-11 items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] pl-4 pr-3 text-[15px] font-semibold text-white transition-colors hover:border-orange-300/40 hover:bg-white/[0.07] focus-visible:border-orange-300/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60"
                : `flex min-h-12 w-full items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 ${
                    isEcole
                      ? "border-school-accent/35 bg-school-blue/20 hover:border-school-accent/60 hover:bg-school-blue/30 focus-visible:ring-school-accent/50"
                      : "border-white/10 bg-white/[0.04] hover:border-orange-300/40 hover:bg-white/[0.07] focus-visible:border-orange-300/50 focus-visible:ring-orange-300/60"
                  }`
            }
            aria-label={`Change level, currently ${selectedLevel.compactLabel}`}
          >
            <span
              className={
                inline ? undefined : "min-w-0 truncate text-base font-black text-white sm:text-lg"
              }
            >
              {selectedLevel.compactLabel}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-white/55" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align={inline ? "center" : "start"}
            sideOffset={8}
            collisionPadding={16}
            className={`z-[10000] ${
              inline ? "w-72" : "w-[var(--radix-dropdown-menu-trigger-width)]"
            } max-w-[calc(100vw-2rem)] rounded-lg border bg-[#09090b] p-2 shadow-[0_20px_60px_rgba(0,0,0,0.55)] ${
              isEcole ? "border-school-blue/40" : "border-white/10"
            }`}
          >
            {LANGUAGE_LEVEL_OPTIONS.map((level) => {
              const isSelected = level.value === value;

              return (
                <DropdownMenu.Item
                  key={level.value}
                  onSelect={() => onValueChange(level.value)}
                  aria-label={level.compactLabel}
                  className={`cursor-pointer rounded-lg border px-3.5 py-3 outline-none transition-colors ${
                    isSelected
                      ? isEcole
                        ? "border-school-accent/35 bg-school-blue/30 text-school-soft"
                        : "border-white bg-white text-black"
                      : `border-transparent text-white/75 ${
                          isEcole
                            ? "hover:bg-school-blue/20 hover:text-school-soft focus:bg-school-blue/20 focus:text-school-soft"
                            : "hover:bg-white/[0.06] hover:text-white focus:border-orange-300/30 focus:bg-orange-500/15 focus:text-orange-100"
                        }`
                  }`}
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="text-sm font-black">{level.compactLabel}</span>
                      <span
                        className={`mt-1 block text-xs leading-5 ${
                          isSelected
                            ? isEcole
                              ? "text-school-soft/70"
                              : "text-black/60"
                            : "text-white/45"
                        }`}
                      >
                        {level.description}
                      </span>
                    </span>
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" aria-hidden="true" />}
                    </span>
                  </span>
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
